import '../icon';
import { defineCustomElement, getBooleanAttribute, getAttribute, updateBooleanAttribute, updateAttribute, NectaryElement, isAttrTrue, getReactEventHandler, isAttrEqual } from '../utils';
const templateHTML = '<style>:host{display:inline}a{font:var(--sinch-comp-link-default-font-initial);font-size:inherit;line-height:inherit;text-decoration:var(--sinch-comp-link-default-text-decoration-initial);color:var(--sinch-comp-link-color-default-text-initial);border-radius:.5em;white-space:nowrap;--sinch-global-color-icon:var(--sinch-comp-link-color-default-icon-initial)}a:hover{text-decoration:var(--sinch-comp-link-default-text-decoration-hover);color:var(--sinch-comp-link-color-default-text-hover);--sinch-global-color-icon:var(--sinch-comp-link-color-default-icon-hover)}a:focus-visible{outline:2px solid var(--sinch-comp-link-color-default-outline-focus);outline-offset:2px}:host([standalone]){display:block}:host([standalone]) a{display:block;font:var(--sinch-comp-link-standalone-font-initial);font-size:inherit;line-height:inherit;text-decoration:none;width:fit-content}#external-icon,#standalone-icon{display:none;height:1em}#icon-prefix{display:none;margin-left:-.25em}:host([external]:not([standalone])) #external-icon{display:inline-block;margin-left:.25em;vertical-align:-.2em;--sinch-global-size-icon:1em}:host([standalone][external]) #external-icon{display:inline-block;vertical-align:-.4em;--sinch-global-size-icon:1.5em}:host([standalone]) #icon-prefix{display:inline}:host([standalone]:not([external])) #standalone-icon{display:inline-block;vertical-align:-.4em;--sinch-global-size-icon:1.5em}:host([disabled]) a{color:var(--sinch-comp-link-color-disabled-text-initial);pointer-events:none;cursor:initial;text-decoration:var(--sinch-comp-link-default-text-decoration-disabled);--sinch-global-color-icon:var(--sinch-comp-link-color-disabled-icon-initial)}#content{white-space:var(--sinch-global-text-white-space,normal)}</style><a referrerpolicy="no-referer" aria-hidden="true"><span id="content"></span><span id="icon-prefix">&nbsp;</span><sinch-icon id="external-icon" name="open_in_new"></sinch-icon><sinch-icon id="standalone-icon" name="arrow_forward"></sinch-icon></a>';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-link', class extends NectaryElement {
  #$anchor;
  #$text;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$anchor = shadowRoot.querySelector('a');
    this.#$text = shadowRoot.querySelector('#content');
  }
  connectedCallback() {
    this.setAttribute('role', 'link');
    this.#$anchor.addEventListener('click', this.#onAnchorClick);
    this.#$anchor.addEventListener('focus', this.#onAnchorFocus);
    this.#$anchor.addEventListener('blur', this.#onAnchorBlur);
    this.addEventListener('-click', this.#onClickReactHandler);
    this.addEventListener('-focus', this.#onFocusReactHandler);
    this.addEventListener('-blur', this.#onBlurReactHandler);
  }
  disconnectedCallback() {
    this.#$anchor.removeEventListener('click', this.#onAnchorClick);
    this.#$anchor.removeEventListener('focus', this.#onAnchorFocus);
    this.#$anchor.removeEventListener('blur', this.#onAnchorBlur);
    this.removeEventListener('-click', this.#onClickReactHandler);
    this.removeEventListener('-focus', this.#onFocusReactHandler);
    this.removeEventListener('-blur', this.#onBlurReactHandler);
  }
  static get observedAttributes() {
    return ['text', 'href', 'use-history', 'external', 'standalone', 'disabled'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    if (isAttrEqual(oldVal, newVal)) {
      return;
    }
    switch (name) {
      case 'text':
        {
          this.#$text.textContent = newVal;
          break;
        }
      case 'href':
        {
          updateAttribute(this.#$anchor, 'href', newVal);
          break;
        }
      case 'use-history':
        {
          const shouldUseHistory = isAttrTrue(newVal);
          if (shouldUseHistory) {
            updateBooleanAttribute(this, 'preventdefault', true);
          }
          updateBooleanAttribute(this, name, shouldUseHistory);
          break;
        }
      case 'standalone':
      case 'disabled':
        {
          updateBooleanAttribute(this, name, isAttrTrue(newVal));
          break;
        }
      case 'external':
        {
          const isExternal = isAttrTrue(newVal);
          updateAttribute(this.#$anchor, 'target', isExternal ? '_blank' : null);
          updateBooleanAttribute(this, name, isExternal);
          break;
        }
    }
  }
  get text() {
    return getAttribute(this, 'text', '');
  }
  set text(value) {
    updateAttribute(this, 'text', value);
  }
  get href() {
    return getAttribute(this, 'href', '');
  }
  set href(value) {
    updateAttribute(this, 'href', value);
  }
  set 'use-history'(value) {
    updateBooleanAttribute(this, 'use-history', value);
  }
  get 'use-history'() {
    return getBooleanAttribute(this, 'use-history');
  }
  set disabled(isDisabled) {
    updateBooleanAttribute(this, 'disabled', isDisabled);
  }
  get disabled() {
    return getBooleanAttribute(this, 'disabled');
  }
  set external(isExternal) {
    updateBooleanAttribute(this, 'external', isExternal);
  }
  get external() {
    return getBooleanAttribute(this, 'external');
  }
  set standalone(isstandalone) {
    updateBooleanAttribute(this, 'standalone', isstandalone);
  }
  get standalone() {
    return getBooleanAttribute(this, 'standalone');
  }
  set preventDefault(isPrevented) {
    updateBooleanAttribute(this, 'preventdefault', isPrevented);
  }
  get preventDefault() {
    return getBooleanAttribute(this, 'preventdefault');
  }
  get focusable() {
    return true;
  }
  focus() {
    this.#$anchor.focus();
  }
  blur() {
    this.#$anchor.blur();
  }
  #onAnchorClick = e => {
    if (this.preventDefault) {
      e.preventDefault();
      if (this['use-history']) {
        history.pushState({}, '', this.href);
      }
    }
    this.dispatchEvent(new CustomEvent('-click'));
  };
  #onAnchorFocus = () => {
    this.dispatchEvent(new CustomEvent('-focus'));
  };
  #onAnchorBlur = () => {
    this.dispatchEvent(new CustomEvent('-blur'));
  };
  #onFocusReactHandler = () => {
    getReactEventHandler(this, 'on-focus')?.();
  };
  #onBlurReactHandler = () => {
    getReactEventHandler(this, 'on-blur')?.();
  };
  #onClickReactHandler = e => {
    getReactEventHandler(this, 'on-click')?.(e);
  };
});